import { colors, LocalStorage, SessionStorage } from "quasar";
import Meeting from "../libs/deepstream/meeting";
import Preview from "../libs/deepstream/preview";
import axios from "axios";

const getDefaultState = () => {
  return {
    config: {},
    isOffline: false,
    offlineData: {},
    oops: false,
    screenErrorType: null,
    browserInvalid: false,
    errorMessages: {
      desktopSize: {
        textBold: "Please enlarge the browser window and reload the page.",
        text: "Your current screen size is not supported.",
        icon: {
          name: "aspect_ratio",
        },
      },
      landscape: {
        textBold: "Please rotate your device to portrait and refresh the page.",
        text: "Landscape view is not supported.",
        icon: {
          name: "screen_rotation",
          style: "transform: rotate(90deg);",
        },
      },
      browser: {
        textBold: "Sorry, this browser is not supported.",
        text: "Please, try it from Google Chrome, Mozilla Firefox, Edge or Safari.",
        icon: {
          name: "web_asset_off",
        },
      },
    },
    fromAgent: false,
    agentAccountId: null,
    agentAccountCid: null,
    businessCard: null,
    waitForBrandSelection: false,
  };
};

export default {
  namespaced: true,

  state: getDefaultState(),

  mutations: {
    config(state, config) {
      state.config = config;
    },
    reset(state) {
      Object.assign(state, getDefaultState());
    },
    isOffline(state, payload) {
      state.isOffline = payload;
    },
    offlineData(state, offlineData) {
      LocalStorage.set("offline_data", offlineData);
      state.offlineData = offlineData;
    },
    oops(state, oops) {
      state.oops = oops;
    },
    screenErrorType(state, screenErrorType) {
      state.screenErrorType = screenErrorType;
    },
    browserInvalid(state, browserInvalid) {
      state.browserInvalid = browserInvalid;
    },
    fromAgent(state, fromAgent) {
      state.fromAgent = fromAgent;
    },
    agentAccountId(state, agentAccountId) {
      state.agentAccountId = agentAccountId;
    },
    agentAccountCid(state, agentAccountCid) {
      state.agentAccountCid = agentAccountCid;
    },
    businessCard(state, businessCard) {
      state.businessCard = businessCard;
    },
    waitForBrandSelection(state, waitForBrandSelection) {
      state.waitForBrandSelection = waitForBrandSelection;
    },
  },

  getters: {
    isOffline: (state) => state.isOffline,
    businessCard: (state) => {
      // load agent from meeting,preview or offline data
      const offlineData = LocalStorage.getItem("offline_data");
      const fakeAgent = SessionStorage.getItem("fakeAgent");
      const meeting = Meeting.getInstance().meeting;
      const preview = Preview.getInstance().meeting;
      let disableLinks = false;
      let agent = null;
      if (meeting !== null) {
        // if meeting is active, use agent from meeting
        agent = meeting.agent;
        disableLinks = true;
      } else {
        // if meeting is not active, use agent from preview
        if (preview !== null) {
          agent = preview.agent;
          disableLinks = true;
        } else {
          // if preview is not active, use agent from offline data
          if (offlineData) {
            agent = offlineData.agent;
          } else if (fakeAgent) {
            agent = fakeAgent;
          } else {
            // if no offline data is found, return null
            return null;
          }
        }
      }
      // once the agent data is loaded, build the business card
      const config = state.config.businessCard;

      let logo = state.businessCard?.logo;

      if (!logo) {
        logo = {
          logo: !!config.logoImage?.src
            ? `${state.config.cdnUrl}${config.logoImage.src}`
            : null,
          style: config.logoImage.style,
        };
      }

      if (!state.businessCard) {
        return {
          logo,
          logoStyles: {
            style: config.logoImage.style,
            travelAgentBannerStyle: config.logoImage.travelAgentBannerStyle,
          },
          name: `${agent.firstName} ${agent.lastName}`,
          lastName: agent.lastName ?? "",
          title: agent.title ?? "",
          phone: agent.phoneNumber ?? "",
          nickname: agent.nickname ?? "",
          email: agent.email ?? "",
          avatar: {},
          blocks: config.customBlocks,
          disableLinks: disableLinks,
          useNickname: config.useNickname,
          additionalPhones: [],
        };
      }

      return {
        logo,
        logoStyles: {
          style: config.logoImage.style,
          travelAgentBannerStyle: config.logoImage.travelAgentBannerStyle,
        },
        name: state.businessCard.name,
        lastName: state.businessCard.family_name,
        title: state.businessCard.title,
        phone: state.businessCard.phoneNumber,
        nickname: state.businessCard.nickname,
        email: state.businessCard.email,
        avatar: state.businessCard.avatar,
        blocks: config.customBlocks,
        disableLinks: disableLinks,
        useNickname: config.useNickname,
        additionalPhones: state.businessCard.additionalPhones ?? [],
      };
    },
  },

  actions: {
    async siteConfiguration({ commit, state }, account_id) {
      const params = {
        name: "consumer",
        forceOrigin: LocalStorage.getItem("forceOrigin"),
      };

      if (account_id) {
        params.account_id = account_id;
      }

      const config = await axios(
        `${process.env.API_BASE_URL}/site-configuration/application`,
        { params }
      );
      if (config.status !== 200 || !config.data.data) throw "404";
      commit("config", config.data.data);

      // load colors configurations
      if (state.config.colors) {
        const colorList = state.config.colors;
        for (const key in colorList) {
          colors.setBrand(key, colorList[key]);
        }
      }
      // load fonts configurations
      if (state.config.fonts) {
        document.documentElement.style.setProperty(
          "--bodyFontFamily",
          state.config.fonts.body.fontStyle.fontFamily
        );
        document.documentElement.style.setProperty(
          "--bodyFontStyle",
          state.config.fonts.body.fontStyle.fontStyle
        );
        document.documentElement.style.setProperty(
          "--bodyFontWeight",
          state.config.fonts.body.fontStyle.fontWeight
        );
        document.documentElement.style.setProperty(
          "--bodyFontSize",
          state.config.fonts.body.fontStyle.fontSize
        );
      }
    },
    isOffline({ commit }, payload) {
      commit("isOffline", payload);
    },
    async sendReview({ state }, rating) {
      const offlineData = LocalStorage.getItem("offline_data");

      if (!offlineData) {
        return;
      }

      if (rating.feedback > 0) {
        await fetch(`${process.env.API_BASE_URL}/consumer/review`, {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sid: offlineData.sid,
            rating: rating,
            agent: offlineData.agent,
          }),
        });
      }
      offlineData.review = true;
      LocalStorage.set("offline_data", offlineData);
    },
    async validateSiteConfiguration({ state, dispatch }) {
      let account_cid =
        Meeting.getInstance().record?.record?.data?.agent?.account_cid;
      let account_id =
        Meeting.getInstance().record?.record?.data?.agent?.account_id;

      if (state.fromAgent) {
        account_cid = state.agentAccountCid;
        account_id = state.agentAccountId;
      }

      const offlineData = LocalStorage.getItem("offline_data");
      if (!offlineData?.forceOrigin === "true") {
        LocalStorage.set("forceOrigin", offlineData.forceOrigin);
      }

      if (offlineData?.agent?.account_cid) {
        account_cid = offlineData.agent.account_cid;
      }

      if (!account_cid || state.config.cid === account_cid) {
        return;
      }

      if (!account_id && offlineData?.agent.account_id) {
        account_id = offlineData.agent.account_id;
      }

      await dispatch("siteConfiguration", account_id);
    },
    async getBusinessCard({ state, commit }, email) {
      let brand;

      const offlineData = LocalStorage.getItem("offline_data");

      if (offlineData) {
        if (typeof offlineData === "string") {
          brand = JSON.parse(offlineData).brand ?? {};
        } else {
          brand = offlineData.brand ?? {};
        }
      } else if (state.fromAgent) {
        brand = JSON.parse(
          Preview.getInstance().record.record?.data?.brand ?? "{}"
        );
      } else {
        brand = JSON.parse(
          Meeting.getInstance().record.record?.data?.brand ?? "{}"
        );
      }

      const response = await axios.get(
        `${process.env.USER_ADMIN_API_URL}/users/businessCard/getCard`,
        { params: { email, cid: brand.cid ?? state.config.cid } }
      );

      commit("businessCard", response.data);
    },
  },
};
